import { encodeAbiParameters, encodeFunctionData, stringToHex } from "viem";

import {
  swapRouter02,
  v3Factory,
  v3Migrator,
  v3NFTDescriptor,
  v3NonfungiblePositionManager,
  v3NonfungibleTokenPositionDescriptor,
  v3QuoterV2,
  v3Staker,
  v3SwapRouter,
  v3TickLens,
} from "../contracts/artifacts";

import { deployContract, deployRaw, linkLibrary } from "./deploy-utils";

import type { DeployContext } from "./deploy-utils";
import type {
  DeploymentPhaseResult,
  FoundationAddresses,
  V2Addresses,
  V3Addresses,
} from "./types";
import type { Hash, Hex } from "viem";

/**
 * Default max incentive start lead time for UniswapV3Staker (30 days).
 * Matches the mainnet deployment value.
 */
const MAX_INCENTIVE_START_LEAD_TIME = 60 * 60 * 24 * 30; // 2_592_000 seconds

/**
 * Default max incentive duration for UniswapV3Staker (180 days).
 * Matches the mainnet deployment value.
 */
const MAX_INCENTIVE_DURATION = 60 * 60 * 24 * 180; // 15_552_000 seconds

/**
 * Native currency label for NonfungibleTokenPositionDescriptor.
 * "ETH" encoded as bytes32.
 */
const NATIVE_CURRENCY_LABEL = stringToHex("ETH", { size: 32 });

/**
 * Phase 3: Deploy Uniswap V3 contracts.
 *
 * Deploys the full V3 stack in dependency order:
 *
 * 1. UniswapV3Factory (no constructor args)
 * 2. NFTDescriptor library (no constructor args)
 * 3. NonfungibleTokenPositionDescriptor(weth9, nativeCurrencyLabelBytes)
 *    — requires NFTDescriptor library linked
 * 4. NonfungiblePositionManager(factory, weth9, positionDescriptor)
 * 5. SwapRouter(factory, weth9)
 * 6. QuoterV2(factory, weth9)
 * 7. TickLens (no constructor args)
 * 8. V3Migrator(factory, weth9, positionManager)
 * 9. UniswapV3Staker(factory, positionManager, maxStartLeadTime, maxDuration)
 * 10. SwapRouter02(v2Factory, v3Factory, positionManager, weth9)
 *
 * @param ctx - Deploy context
 * @param foundation - Phase 1 addresses (WETH9)
 * @param v2 - Phase 2 addresses (V2 factory for SwapRouter02)
 */
export async function deployV3(
  ctx: DeployContext,
  foundation: FoundationAddresses,
  v2: V2Addresses,
): Promise<DeploymentPhaseResult<V3Addresses>> {
  const start = Date.now();
  let totalGas = 0n;
  const txHashes: Hash[] = [];

  function track(result: { gasUsed: bigint; txHash: Hash }): void {
    totalGas += result.gasUsed;
    txHashes.push(result.txHash);
  }

  // 1. UniswapV3Factory — no constructor args
  const factoryResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3Factory(),
  );
  track(factoryResult);

  // 1b. Enable 1bp fee tier (100 / tickSpacing 1) — not in default constructor
  const enableFeeData = encodeFunctionData({
    abi: [
      {
        type: "function",
        name: "enableFeeAmount",
        inputs: [
          { name: "fee", type: "uint24" },
          { name: "tickSpacing", type: "int24" },
        ],
        outputs: [],
        stateMutability: "nonpayable",
      },
    ] as const,
    functionName: "enableFeeAmount",
    args: [100, 1],
  });
  const enableFeeTxHash = await ctx.walletClient.sendTransaction({
    account: ctx.deployer,
    to: factoryResult.address,
    data: enableFeeData,
    chain: ctx.walletClient.chain,
  });
  const enableFeeReceipt = await ctx.publicClient.waitForTransactionReceipt({
    hash: enableFeeTxHash,
  });
  totalGas += enableFeeReceipt.gasUsed;
  txHashes.push(enableFeeTxHash);

  // 2. NFTDescriptor library — no constructor args
  const nftDescriptorResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3NFTDescriptor(),
  );
  track(nftDescriptorResult);

  // 3. NonfungibleTokenPositionDescriptor(weth9, nativeCurrencyLabelBytes)
  //    Requires NFTDescriptor library linked into bytecode
  const positionDescriptorArtifact = v3NonfungibleTokenPositionDescriptor();
  const linkedBytecode = linkLibrary(
    positionDescriptorArtifact.bytecode,
    "contracts/libraries/NFTDescriptor.sol:NFTDescriptor",
    nftDescriptorResult.address,
  );

  // Encode constructor args manually and append to linked bytecode
  const posDescriptorArgs = encodeAbiParameters(
    [
      { name: "_WETH9", type: "address" },
      { name: "_nativeCurrencyLabelBytes", type: "bytes32" },
    ],
    [foundation.weth9, NATIVE_CURRENCY_LABEL],
  );
  const posDescriptorData = (linkedBytecode +
    posDescriptorArgs.slice(2)) as Hex;

  const positionDescriptorResult = await deployRaw(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    posDescriptorData,
  );
  track(positionDescriptorResult);

  // 4. NonfungiblePositionManager(factory, weth9, positionDescriptor)
  const positionManagerResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3NonfungiblePositionManager(),
    [factoryResult.address, foundation.weth9, positionDescriptorResult.address],
  );
  track(positionManagerResult);

  // 5. SwapRouter(factory, weth9)
  const swapRouterResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3SwapRouter(),
    [factoryResult.address, foundation.weth9],
  );
  track(swapRouterResult);

  // 6. QuoterV2(factory, weth9)
  const quoterV2Result = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3QuoterV2(),
    [factoryResult.address, foundation.weth9],
  );
  track(quoterV2Result);

  // 7. TickLens — no constructor args
  const tickLensResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3TickLens(),
  );
  track(tickLensResult);

  // 8. V3Migrator(factory, weth9, positionManager)
  const migratorResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3Migrator(),
    [factoryResult.address, foundation.weth9, positionManagerResult.address],
  );
  track(migratorResult);

  // 9. UniswapV3Staker(factory, positionManager, maxStartLeadTime, maxDuration)
  const stakerResult = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    v3Staker(),
    [
      factoryResult.address,
      positionManagerResult.address,
      BigInt(MAX_INCENTIVE_START_LEAD_TIME),
      BigInt(MAX_INCENTIVE_DURATION),
    ],
  );
  track(stakerResult);

  // 10. SwapRouter02(v2Factory, v3Factory, positionManager, weth9)
  const swapRouter02Result = await deployContract(
    ctx.publicClient,
    ctx.walletClient,
    ctx.deployer,
    swapRouter02(),
    [
      v2.factory,
      factoryResult.address,
      positionManagerResult.address,
      foundation.weth9,
    ],
  );
  track(swapRouter02Result);

  return {
    phase: 3,
    name: "Uniswap V3",
    addresses: {
      factory: factoryResult.address,
      router: swapRouterResult.address,
      quoterV2: quoterV2Result.address,
      nonfungiblePositionManager: positionManagerResult.address,
      nonfungibleTokenPositionDescriptor: positionDescriptorResult.address,
      tokenDescriptor: nftDescriptorResult.address,
      tickLens: tickLensResult.address,
      migrator: migratorResult.address,
      staker: stakerResult.address,
      swapRouter02: swapRouter02Result.address,
      owner: ctx.deployer,
    },
    gasUsed: totalGas,
    durationMs: Date.now() - start,
    txHashes,
  };
}
